import { ChangeDetectionStrategy, Component, EventEmitter, Input, NgZone, OnDestroy, Output } from '@angular/core';

import { NgClass, NgIf } from '@angular/common';
import { isEqual } from 'lodash-es';
import { ShHtmlParserComponent } from '../../../core/html-parser/html-parser.component';
import { ShIconComponent } from '../../../elements/components/icon/icon.component';
import { NotificationType } from '../../../elements/components/notification/entities/notification-types.entity';
import { NavigationLinkDirective } from '../../../elements/directives/navigation-link/navigation-link.directive';
import { Timer } from '../../entities/timer.entity';
import { Toast } from '../../entities/toast.entity';

@Component({
    selector: 'sh-toast',
    templateUrl: './toast.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [NgIf, NgClass, ShIconComponent, ShHtmlParserComponent, NavigationLinkDirective]
})
export class ShToastComponent implements OnDestroy {
    private readonly BASE_CLASS: string = 'sh-toast';

    currentToastClass: string = this.BASE_CLASS;
    currentToast!: Toast;
    private timer: Timer | null = null;

    constructor(private readonly ngZone: NgZone) {}

    @Input()
    set toast(toast: Toast) {
        if (!this.currentToast || !isEqual(this.currentToast, toast)) {
            this.updateToast(toast);
            this.setTimer(toast);
        }
    }

    @Output() toastClosed: EventEmitter<boolean> = new EventEmitter<boolean>();

    closeToast(event: Event): void {
        this.toastClosed.emit(true);
    }

    onFocus(): void {
        if (this.timer) {
            this.timer.pause();
        }
    }

    onFocusOut(): void {
        if (this.timer) {
            this.timer.resume(1000);
        }
    }

    ngOnDestroy(): void {
        this.stopAndUnsetTimer();
    }

    private stopAndUnsetTimer(): void {
        if (this.timer) {
            this.timer.stop();
            this.timer = null;
        }
    }

    private setTimer(toast: Toast): void {
        this.stopAndUnsetTimer();

        if (toast.autoClose) {
            this.timer = new Timer(() => this.toastClosed.emit(true), toast.autoCloseDelay, this.ngZone);
        }
    }

    private updateToast(toast: Toast): void {
        this.currentToast = toast;
        this.currentToastClass = this.determineToastClassByType(toast.type);
    }

    private determineToastClassByType(type: NotificationType): string {
        switch (type) {
            case NotificationType.Error:
                return `${this.BASE_CLASS}--error`;
            case NotificationType.Warning:
                return `${this.BASE_CLASS}--warning`;
            case NotificationType.Success:
                return `${this.BASE_CLASS}--success`;
            case NotificationType.Update:
                return `${this.BASE_CLASS}--update`;
            default:
                return this.BASE_CLASS;
        }
    }
}
